import { getDomainSetting } from '@/lib/getDomainSettings'
import React, { useEffect, useState } from 'react'
import { QueryParamProvider } from 'use-query-params'
import { NextAdapter } from 'next-query-params'
import Head from 'next/head'
import { QueryClientProvider, HydrationBoundary } from '@tanstack/react-query'
import dynamic from 'next/dynamic'

import { ConfirmProvider } from 'material-ui-confirm'
import * as Sentry from '@sentry/react'
import NextApp from 'next/app'
import { DefaultSeo } from 'next-seo'
import { loadReCaptcha } from 'react-recaptcha-google'
import { useRouter } from 'next/router'
import { addMinutes, toDate } from 'date-fns'
import cookie from 'js-cookie'

import Layout from '@/components/Common/Layout/Layout'

import getLocaleFromCtx from '@/lib/getLocaleFormCtx'
import SETTINGS from '@/src/settings'
import useRouterScroll from '@/lib/hook/useRouterScroll'
import getEnvVar from '@/lib/getEnvVar'
import DeployRefreshManager from '@/components/refreshApp/DeployRefreshManager'
import { getQueryClient } from '@/lib/hook/tanstackQuery'

import '../public/css/autocomplete-theme.css'
import './global.css'
import '../public/css/toasters.css'
import 'react-multi-carousel/lib/styles.css'
import '../public/css/carousel.css'
import '../public/css/autocomplete.css'
import '../public/css/select-fix.css'

import packageJson from '../package.json'
import { GoogleOAuthProvider } from '@react-oauth/google'

// Devtools react-query : client-only. Un rendu SSR declenche "No QueryClient set"
// car @tanstack/react-query-devtools resout une autre instance du contexte
// @tanstack/react-query pendant le SSR (skew de version 5.91 vs 5.90).
const ReactQueryDevtools = dynamic(
  () => import('@tanstack/react-query-devtools').then(m => m.ReactQueryDevtools),
  { ssr: false }
)

const languagesAvailable = [
  { lang_code: 'fr', name: 'French', country_code: 'FR', is_default: true },
  { lang_code: 'en', name: 'English', country_code: 'EN', is_default: false },
]

if (getEnvVar('APP_ENV') === 'production' || getEnvVar('APP_ENV') === 'staging') {
  // @ts-ignore
  Sentry.init({
    dsn: 'https://9d1079efdd7a445aa7a6044988ae99ab@o395581.ingest.sentry.io/5247683',
    tracesSampleRate: 1.0,
    release: 'pharmedistore@' + packageJson.version,
    environment: getEnvVar('APP_ENV'),
    //ignoreErrors: ['404'],
    replaysSessionSampleRate: 0.1,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
    // replayIntegration retire de l'init : charge en idle ci-dessous pour ne pas
    // bloquer le thread principal au demarrage (gain FID/INP).
  })

  if (typeof window !== 'undefined') {
    const loadReplay = () => {
      const client = Sentry.getClient()
      client?.addIntegration?.(Sentry.replayIntegration())
    }
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadReplay)
    } else {
      setTimeout(loadReplay, 3000)
    }
  }
}

const App = ({ Component, pageProps, locale }) => {
  const router = useRouter()
  // Client stable par app cote navigateur, per-request cote serveur.
  const [queryClient] = useState(() => getQueryClient())

  const hostName = pageProps.host || 'pharmedistore.com'

  const customSeoConfig = {
    ...SETTINGS.seo,
    title: hostName,
    titleTemplate: `%s | ${hostName}`,
    defaultTitle: hostName,
  }

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side')
    if (jssStyles) {
      jssStyles.parentNode.removeChild(jssStyles)
    }
    loadReCaptcha()
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const regex = new RegExp('customer', 'g')
      if (!regex.test(router.route))
        cookie.set('referer', window.location.href, { expires: toDate(addMinutes(new Date(), 10)) })
    }
  }, [router.asPath])

  useRouterScroll()

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
        />
      </Head>
      <DefaultSeo {...customSeoConfig} />
      <GoogleOAuthProvider clientId={getDomainSetting('google').client_id}>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={pageProps.dehydratedState}>
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
            <QueryParamProvider adapter={NextAdapter}>
              <DeployRefreshManager />
              <ConfirmProvider
                defaultOptions={{
                  title: 'Confirmation',
                  confirmationText: 'Confirmer',
                  cancellationText: 'Annuler',
                }}
              >
                <Layout locale={locale} host={pageProps.host}>
                  <Component {...pageProps} />
                </Layout>
              </ConfirmProvider>
            </QueryParamProvider>
          </HydrationBoundary>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </>
  )
}

App.getInitialProps = async appContext => {
  const appProps = await NextApp.getInitialProps(appContext)

  if (appContext.ctx && appContext.ctx.req?.headers.host === 'pharmedistore.com') {
    const newUrl = `https://www.${appContext.ctx.req.headers.host}${appContext.ctx.req.url}`
    appContext.ctx.res.writeHead(301, { Location: newUrl })
    appContext.ctx.res.end()
  }

  let locale = getLocaleFromCtx(appContext.ctx)

  // Protect lang not included in Administration
  if (![...languagesAvailable, SETTINGS.intlAdmin].map(i => i.lang_code).includes(locale))
    locale = SETTINGS.localeDefault

  return {
    ...appProps,
    locale,
    host: appContext.ctx.req?.headers.host,
  }
}
export default Sentry.withProfiler(App)
